import type { Customer } from 'src/data/customer';
import { isPlanFromBusinessCategory, type SubscriptionPlan } from 'src/data/subscription';

export interface FeatureLimit {
  name: FeatureLimitName;
  limit: number;
  currentCount: number;
  adminUpdated: boolean;
}

/**
 * Feature limits
 */
export const FEATURE_LIMITS = {
  tonesOfVoicePerCustomer: 'custom_tones_of_voice_per_customer',
  audiencePersonasPerCustomer: 'audience_personas_per_customer',
  brandBriefPerCustomer: 'brand_brief_per_customer',
  customFormulasPerCustomer: 'custom_formulas_per_customer',
  brandRulesPerCustomer: 'brand_rules_per_customer',
  workspacesPerCustomer: 'workspaces_per_customer',
  membersPerCustomer: 'members_per_customer',
  plagiarismChecksPerCustomer: 'plagiarism_checks_per_customer',
  resourcesRowsPerCustomer: 'resources_rows_per_customer',
  brandVoicePerCustomer: 'brand_voice_per_customer',
  visualLanguagePerCustomer: 'visual_language_per_customer',
  liveScoring: 'live_scoring',
  performancePredictions: 'performance_prediction_per_customer',

  // Deprecated
  // resourcesPerCustomer: 'resources_per_customer',
  // boostPerformancesPerCustomer: 'boost_performances_per_customer',
} as const;
export type FeatureLimitName = (typeof FEATURE_LIMITS)[keyof typeof FEATURE_LIMITS];

export const getFeatureLimitsData = (customer: Customer, feature: FeatureLimitName) => {
  const featureLimits = customer?.featureLimits || [];
  return featureLimits.find((f) => f.name === feature);
};

export const getFeatureLimitedAccount = (customer: Customer) => {
  return customer?.rowsAllocationPerChannel?.find((r) => r.rowsCount < r.rowsAllocated);
};

interface FeatureLimitProps {
  label: string;
  upgradeCta?: string;
}

const FEATURE_LIMITS_PROPS: Record<string, FeatureLimitProps> = {
  [FEATURE_LIMITS.tonesOfVoicePerCustomer]: {
    label: 'Tones of Voice',
  },
  [FEATURE_LIMITS.audiencePersonasPerCustomer]: {
    label: 'Target audiences',
  },
  [FEATURE_LIMITS.brandBriefPerCustomer]: {
    label: 'Messaging Bank assets',
  },
  [FEATURE_LIMITS.customFormulasPerCustomer]: {
    label: 'Custom Formulas',
  },
  [FEATURE_LIMITS.brandRulesPerCustomer]: {
    label: 'Brand Terms',
  },
  [FEATURE_LIMITS.workspacesPerCustomer]: {
    label: 'Workspaces',
  },
  [FEATURE_LIMITS.membersPerCustomer]: {
    label: 'Members',
  },
  [FEATURE_LIMITS.plagiarismChecksPerCustomer]: {
    label: 'Plagiarism Checks',
  },
  [FEATURE_LIMITS.resourcesRowsPerCustomer]: {
    label: 'Rows',
    upgradeCta: 'Upgrade your plan and get more insights, better decisions, and personalized text.',
  },
  [FEATURE_LIMITS.brandVoicePerCustomer]: {
    label: 'Brand Voices',
  },
  [FEATURE_LIMITS.visualLanguagePerCustomer]: {
    label: 'Visual Languages',
  },
  [FEATURE_LIMITS.liveScoring]: {
    label: 'Live Scoring',
  },
  [FEATURE_LIMITS.performancePredictions]: {
    label: 'Performance Predictions',
    upgradeCta:
      'Upgrade your plan to get more performance predictions and additional premium features',
  },
};

export const getFeatureLimitsLabel = (feature: FeatureLimitName) =>
  FEATURE_LIMITS_PROPS[feature]?.label;

export const FEATURE_LIMIT_CHECK_SOURCE = {
  generationFlow: 'generationFlow',
} as const;
export type FeatureLimitCheckSource =
  (typeof FEATURE_LIMIT_CHECK_SOURCE)[keyof typeof FEATURE_LIMIT_CHECK_SOURCE];

export const featureHasUsages = (featureData: FeatureLimit) => {
  if (!featureData || !featureData.limit) {
    return true;
  }
  return (featureData.currentCount || 0) < featureData.limit;
};

export const getFeatureFullSubtitle = ({
  feature,
  limit,
  currentCount,
  inTrial,
}: {
  feature: FeatureLimitName;
  limit: number;
  currentCount: number;
  inTrial: boolean;
}) => {
  switch (feature) {
    case FEATURE_LIMITS.liveScoring:
      return 'Upgrade to get real-time score';

    case FEATURE_LIMITS.performancePredictions:
      if (currentCount >= limit) {
        const postfix = inTrial
          ? 'performance predictions in this trial'
          : 'monthly performance predictions';
        return `You’ve reached your limit of ${limit} ${postfix}`;
      } else {
        return `Upgrade to get more than ${limit} monthly performance predictions`;
      }

    default:
      return null;
  }
};

export const getFeatureFullDescription = ({
  feature,
  plan,
}: {
  feature: FeatureLimitName;
  plan: SubscriptionPlan;
}) => {
  switch (feature) {
    case FEATURE_LIMITS.brandVoicePerCustomer:
      return `To add additional brand voices, upgrade to the ${
        isPlanFromBusinessCategory(plan) ? 'Enterprise' : 'Business'
      } tier.`;

    case FEATURE_LIMITS.visualLanguagePerCustomer:
      return `To add additional visual languages, upgrade to the ${
        isPlanFromBusinessCategory(plan) ? 'Enterprise' : 'Business'
      } tier.`;

    case FEATURE_LIMITS.liveScoring:
      return 'Get real-time scoring and analytics for every copy edit you make.';

    default:
      return FEATURE_LIMITS_PROPS[feature]?.upgradeCta || '';
  }
};
